import { Components } from '@/api/generated/api';
import { SeatType } from '@/api/generated/api.enum';
import { ceilToStep, roundToNearestStep } from '@/utils/number.utils';
import {
  DEFAULT_CURRENCY,
  MAX_SEAT_QUANTITY,
  MIN_STUDENT_QUANTITY,
  MIN_TEAM_QUANTITY,
  STUDENT_QUANTITY_STEP,
} from 'common/constants/billing.constants';
import { applyPercentDiscount, computeGraduatedSeatTotalCents } from 'common/utils/billing.utils';
import { type BillingInterval, type BillingPrice, type MonthlyBillingPrice, type ProductWithMeta } from './types';

export {
  DEFAULT_CURRENCY,
  MAX_SEAT_QUANTITY,
  MIN_STUDENT_QUANTITY,
  MIN_TEAM_QUANTITY,
  STUDENT_QUANTITY_STEP,
  TEAM_QUANTITY_STEP,
} from 'common/constants/billing.constants';
import { clampNumber } from 'common/utils/math.utils';

export { applyPercentDiscount };

export function constrainSeatQuantity(params: {
  value: number;
  minValue: number;
  step: number;
  ceilToStep?: boolean;
}): number {
  const { value, minValue, step, ceilToStep: shouldCeilToStep } = params;
  const roundedValue = shouldCeilToStep ? ceilToStep(value, step) : roundToNearestStep(value, step);
  return clampNumber(roundedValue, minValue, MAX_SEAT_QUANTITY);
}

export function getMinTeamQuantity(userSeatsUsed: number): number {
  return Math.max(MIN_TEAM_QUANTITY, userSeatsUsed);
}

export function getMinStudentQuantity(studentSeatsUsed: number): number {
  const minimumRequiredStudents = Math.max(MIN_STUDENT_QUANTITY, studentSeatsUsed);
  return Math.ceil(minimumRequiredStudents / STUDENT_QUANTITY_STEP) * STUDENT_QUANTITY_STEP;
}

/**
 * Base → discounted price of one product/quantity/interval — THE display math shared by the plan
 * selection step, the express checkout, the order summary and the purchase analytics.
 */
export function computeProductPriceBreakdownCents(
  product: ProductWithMeta,
  ctx: { team?: number; students?: number },
  opts: { interval?: BillingInterval; discountPercent?: number } = {},
): { baseCents: number; totalCents: number; discountCents: number } {
  const discountPercent = opts.discountPercent ?? 0;
  const baseCents = computeProductPriceCents(product, ctx, opts.interval ?? 'year');
  const totalCents = discountPercent > 0 ? applyPercentDiscount(baseCents, discountPercent) : baseCents;
  return { baseCents, totalCents, discountCents: baseCents - totalCents };
}

export function toMonthlyEquivalentCents(amountCents: number, interval: BillingInterval = 'year'): number {
  return interval === 'year' ? amountCents / 12 : amountCents;
}

export function roundDiscount(discount: number | null | undefined): number | null {
  if (discount == null) return null;
  return roundToNearestStep(discount, 5);
}

export function getTrialUpsellDiscountPercent(
  product: Pick<Components.Schemas.ProductTypeDto, 'trialUpsellDiscount'>,
  interval: BillingInterval = 'year',
): number {
  if (interval === 'month') return 0;
  return product.trialUpsellDiscount ?? 0;
}

// Generic trial-upsell surfaces have no selected product yet, so they show the highest purchasable product discount.
export function getMaxTrialUpsellDiscountPercent(
  products: Pick<Components.Schemas.ProductTypeDto, 'trialUpsellDiscount'>[],
  interval: BillingInterval = 'year',
): number {
  return products.reduce(
    (maxDiscount, product) => Math.max(maxDiscount, getTrialUpsellDiscountPercent(product, interval)),
    0,
  );
}

export function getDiscountPercentForCheckoutSource(
  source: string | undefined,
  product: Pick<Components.Schemas.ProductTypeDto, 'id' | 'trialUpsellDiscount'>,
  options: {
    interval?: BillingInterval;
    winbackOffer?: Pick<Components.Schemas.BillingWinbackOfferDto, 'discountPercent' | 'eligibleProductTypeIds'> | null;
  } = {},
): number {
  const interval = options.interval ?? 'year';
  if (source === 'trialWinback') {
    if (interval === 'month' || !options.winbackOffer?.eligibleProductTypeIds.includes(product.id)) return 0;
    return options.winbackOffer.discountPercent;
  }
  if (source === 'trialUpsell') return getTrialUpsellDiscountPercent(product, interval);
  return 0;
}

function computeSeatPriceCents(seats: number, price: BillingPrice | MonthlyBillingPrice): number {
  if ('seatTiers' in price && price.seatTiers?.length) {
    return computeGraduatedSeatTotalCents(seats, price.seatTiers);
  }
  return Math.max(0, seats) * (price.perSeatCents ?? 0);
}

/** Lowest per-seat rate of a price — the "ab X" teaser every plan card shows. */
export function getLowestSeatPriceCents(price: Partial<BillingPrice & MonthlyBillingPrice>): number {
  if (price.seatTiers?.length) {
    return Math.min(...price.seatTiers.map((tier) => tier.perSeatCents));
  }
  return price.perSeatCents ?? 0;
}

/** The pricing triple with safe defaults — one place, so the default shape cannot drift per call site. */
export function getPricingParts(product: ProductWithMeta): {
  currency: string;
  yearly: BillingPrice | null;
  monthly: MonthlyBillingPrice | null;
} {
  return product.pricing ?? { currency: DEFAULT_CURRENCY, yearly: null, monthly: null };
}

export function computeProductPriceCents(
  product: ProductWithMeta,
  ctx: { team?: number; students?: number },
  interval: BillingInterval = 'year',
): number {
  const { yearly, monthly } = getPricingParts(product);
  const useMonthly = interval === 'month' && monthly != null;
  const p = useMonthly ? monthly : yearly;
  if (!p) return 0;

  let total = p.amountCents;

  if (product.supportsSeats) {
    if (product.seatType === SeatType.user) {
      const paidSeats = Math.max(0, ctx.team ?? 0);
      total += computeSeatPriceCents(paidSeats, p);
    }

    if (product.seatType === SeatType.student) {
      const students = Math.max(0, ctx.students ?? 0);
      total += computeSeatPriceCents(students, p);
    }
  }

  return total;
}
